import { useHits, useInstantSearch, Highlight } from 'react-instantsearch';
import { FC, useRef } from 'react';
import type { BaseHit, Hit } from 'instantsearch.js';
import type { SendEventForHits } from 'instantsearch.js/es/lib/utils';
import {
  AlgoliaUtil,
  createAlgoliaImageMonitor,
  currentLanguage,
  toProductImageMonitoringHit,
} from '../../../shared/utils';
import { DangerousHTML } from '../../../shared/components/DangerousHTML';
import { AlgoliaProductHit } from '../../../shared/types';
import { LoadGuard } from './LoadGuard';

const getAutocompleteProductImageURL = (imageUrl: string) => {
  try {
    const url = new URL(imageUrl);

    // Shopify CDN serves lightweight `_thumb` variants for file assets.
    // Keep Pimcore and placeholder URLs unchanged because they don't follow that convention.
    if (url.hostname !== 'cdn.shopify.com' || url.pathname.includes('_thumb.')) {
      return imageUrl;
    }

    url.pathname = url.pathname.replace(/(\.[a-zA-Z0-9]{2,4})$/, '_thumb$1');

    return url.toString();
  } catch {
    return imageUrl;
  }
};

const ProductHit: FC<{
  rawHit: Hit<AlgoliaProductHit>;
  algoliaUtil: AlgoliaUtil;
  sendEvent: SendEventForHits;
}> = ({ rawHit, algoliaUtil, sendEvent }) => {
  const hit = algoliaUtil.normalizeProductHit(rawHit);
  const { results } = useInstantSearch();
  const monitorRef = useRef(createAlgoliaImageMonitor());
  const autocompleteProductImageUrl = getAutocompleteProductImageURL(hit.image);

  const price =
    rawHit.availability !== 'unavailable' ? (
      <>
        <b>{hit.price}</b>
        {hit.priceExclVAT && (
          <span className="excl-vat">
            ({hit.priceExclVAT} {theme.locales.exclVAT})
          </span>
        )}
      </>
    ) : null;

  return (
    <div className="aa-suggestion">
      <a
        data-algolia-index={results.index}
        data-algolia-position={rawHit.position}
        data-algolia-queryid={results.queryID}
        data-algolia-objectid={hit.algoliaObjectID}
        className="aa-product"
        href={hit.url}
        onClick={() => {
          hit.onClickProductLink({ results, insightsHit: rawHit, sendEvent });
        }}
        style={{ display: 'block' }}
      >
        <img
          className="aa-product-picture"
          src={autocompleteProductImageUrl}
          alt={hit.imageAltName}
          style={{ objectFit: 'contain', objectPosition: 'center center' }}
          onError={() =>
            monitorRef.current({
              type: 'product_autocomplete',
              reason: 'broken_image_url',
              imageUrl: autocompleteProductImageUrl,
              originComponent: 'AutocompleteSearch/Products/ProductHit',
              hit: toProductImageMonitoringHit(hit),
            })
          }
        />
        <div className="aa-product-text">
          <p className="aa-product-title">
            <Highlight
              hit={rawHit as unknown as Hit<BaseHit>}
              attribute={`title.${currentLanguage}`}
              highlightedTagName="span"
              classNames={{ highlighted: 'aa-highlight' }}
            />
          </p>
          <p className="aa-product-info">
            {hit.sku && (
              <Highlight
                hit={rawHit as unknown as Hit<BaseHit>}
                attribute="sku"
                highlightedTagName="span"
                classNames={{ highlighted: 'aa-highlight' }}
              />
            )}
            {hit.MPN && hit.MPN !== hit.sku && (
              <>
                {' / '}
                <Highlight
                  hit={rawHit as unknown as Hit<BaseHit>}
                  attribute="MPN"
                  highlightedTagName="span"
                  classNames={{ highlighted: 'aa-highlight' }}
                />
              </>
            )}
          </p>
          <p className="aa-product-price">{price}</p>
        </div>
      </a>
    </div>
  );
};

export const ProductResults: FC<{ algoliaUtil: AlgoliaUtil }> = ({ algoliaUtil }) => {
  const { hits, sendEvent } = useHits<AlgoliaProductHit>();

  return (
    <LoadGuard noResultsMessage={<DangerousHTML content={theme.locales.autocomplete.notFound.products} />}>
      <div className="aa-suggestions">
        {hits.map((hit) => (
          <ProductHit key={hit.shopifyObjectID} rawHit={hit} algoliaUtil={algoliaUtil} sendEvent={sendEvent} />
        ))}
      </div>
    </LoadGuard>
  );
};
